/**
 * Whether to show a bidder the first-bid primer.
 *
 * SHARED, because the app and the website both draw this primer and it must be
 * the same rule on both. The STORAGE differs and should — the phone uses its own
 * preferences, the browser uses `localStorage` — but "when does it show" is one
 * decision. See `memory/feedback_no_mechanical_divergence.md`.
 *
 * Lifted out of `apps/bidder/src/prefs/usePreferences.tsx` on 2026-09-08, when
 * the website grew the same primer. It was app-only until then, which is why the
 * website had gone without it.
 */

/** `unknown` until the device's storage has answered. */
export type FirstBidState = 'yes' | 'no' | 'unknown';

/**
 * 🔴 TRI-STATE ON PURPOSE, and this is the half that needs the SAFE direction.
 *
 * The same stored fact feeds two readers that need OPPOSITE answers when
 * storage cannot say:
 *
 *  - this primer must not reappear on every bid forever, so unknown means
 *    "do not show it";
 *  - the app's push permission prompt must not fire early, because iOS asks
 *    exactly once and a denial is permanent, so unknown means "do not prompt"
 *    (`mayPromptForPush`, app-only — there is no web equivalent).
 *
 * Collapsing that into a boolean silently hands one of them the dangerous
 * direction.
 *
 * Unknown costing the primer is cheap: the standing footnote beside it says the
 * same thing in ordinary words, so nobody is left uninformed — they get the
 * plain sentence rather than the pointed one.
 */
export function showsFirstBidPrimer(state: FirstBidState): boolean {
  return state === 'no';
}
